<template>
  <q-select
    ref="select"
    v-bind="$attrs"
    :options="filteredOptions"
    :option-label="optionLabel"
    :multiple="multiple"
    use-input
    :fill-input="singleValueMode"
    :hide-selected="singleValueMode"
    input-debounce="0"
    @filter="onFilter"
  >
    <template v-for="(_, name) in $slots" v-slot:[name]="scope">
      <slot :name="name" v-bind="scope || {}" />
    </template>
    <template v-if="!$slots['no-option']" v-slot:no-option>
      <q-item>
        <q-item-section class="text-grey">{{ $t("main.noResults") }}</q-item-section>
      </q-item>
    </template>
  </q-select>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import { filterOptions } from "@/utils/searchableSelect";

/**
 * Drop-in replacement for q-select that lets the user type to search the options.
 * Every other attribute, event and slot is forwarded to q-select unchanged.
 * See docs/guides/SEARCHABLE_SELECT.md.
 */
@Options({
  name: "SearchableSelect",
  inheritAttrs: false,
  props: {
    options: {
      type: Array,
      default: () => [],
    },
    optionLabel: {
      type: [String, Function],
      default: "label",
    },
    multiple: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    options() {
      this.applyFilter();
    },
  },
})
export default class SearchableSelect extends Vue {
  options!: any[];
  optionLabel!: string | ((option: any) => string);
  multiple!: boolean;

  search = "";
  filteredOptions: any[] = [];

  created() {
    this.applyFilter();
  }

  // The typed text replaces the displayed value only for a single value without a custom
  // display (slot or display-value); otherwise chips / custom rendering would be hidden.
  get singleValueMode() {
    const hasDisplayValue = this.$attrs["display-value"] !== undefined || this.$attrs["displayValue"] !== undefined;
    return !this.multiple && !hasDisplayValue && !this.$slots["selected-item"] && !this.$slots["selected"];
  }

  applyFilter() {
    this.filteredOptions = filterOptions(this.options, this.search, this.optionLabel);
  }

  onFilter(val: string, update: (callback: () => void) => void) {
    update(() => {
      this.search = val;
      this.applyFilter();
    });
  }

  get select(): any {
    return this.$refs.select;
  }

  // q-select methods, so that parents holding a ref keep working
  validate(value?: any) {
    return this.select?.validate(value);
  }

  resetValidation() {
    this.select?.resetValidation();
  }

  focus() {
    this.select?.focus();
  }

  blur() {
    this.select?.blur();
  }

  showPopup() {
    this.select?.showPopup();
  }

  hidePopup() {
    this.select?.hidePopup();
  }

  updateInputValue(value: string, noFilter?: boolean) {
    this.select?.updateInputValue(value, noFilter);
  }
}
</script>
